<template>
  <div class="px-4">
    <div v-if="automations && automations.length" class="space-y-4">
      <!-- Loop through automation groups by state -->
      <div v-for="(group, index) in automationGroups" :key="index">
        <h3 class="mb-2 text-xs uppercase tracking-wider text-gray-600">
          {{ group.title }}
        </h3>
        <div class="space-y-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <div
            v-for="automation in group.items"
            :key="automation.serviceAutomation?.idServiceAutomation"
            class="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700 last:border-b-0"
          >
            <div class="flex-1">
              <div class="flex items-center space-x-2">
                <span class="font-medium text-gray-900 dark:text-gray-100">
                  {{ automation.serviceAutomation?.label }}
                </span>
                <span v-if="automation.stateLabelWithOutput" class="text-xs text-gray-500">
                  ({{ automation.stateLabelWithOutput }})
                </span>
              </div>
              <p v-if="automation.stateLabel || automation.stateTime" class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                <span v-if="automation.stateLabel">{{ automation.stateLabel }}</span>
                <span v-if="automation.stateTime"> {{ formatStateTime(automation.state, automation.stateTime) }}</span>
              </p>
            </div>
            <div v-if="automation.actions && automation.actions.length" class="flex space-x-2">
              <o-button
                v-for="action in automation.actions"
                :key="action.code || action.label"
                size="sm"
                variant="primary"
                :loading="processingAction === `${automation.serviceAutomation?.idServiceAutomation}-${action.code}`"
                @click="handleAction(automation, action)"
              >
                {{ action.label }}
              </o-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="text-center py-8">
      <p class="text-gray-500 dark:text-gray-400">
        {{ $t('aftercare.noAutomations') }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ReservationServiceAutomationState, ReservationServiceAutomationStateLabels } from '~/_api/models/reservation'

const { $api, $date, $i18n, $notification } = useNuxtApp()

interface Props {
  reservation: Record<string, any>
  loading?: boolean
}

interface Emits {
  (e: 'update:reservation', value: any): void
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
})

const emit = defineEmits<Emits>()

const processingAction = ref<string | null>(null)

const automations = computed(() => {
  return props.reservation.automations || []
})

const automationGroups = computed(() => {
  const groups: Record<string, any> = {}

  automations.value.forEach((automation: any) => {
    const state = automation.state

    if (!groups[state]) {
      groups[state] = {
        title: getStateTitle(state, automation.serviceAutomation?.requirementMode?.value),
        items: []
      }
    }

    groups[state].items.push(automation)
  })

  return Object.values(groups).sort((a: any, b: any) => {
    const stateOrder: Record<number, number> = { 0: 1, 1: 2, 2: 0 }
    return (stateOrder[a.items[0].state] || 3) - (stateOrder[b.items[0].state] || 3)
  })
})

function getStateTitle (state: any, requirementMode?: any) {
  if (state != null) {
    return $i18n.t(ReservationServiceAutomationStateLabels[state as ReservationServiceAutomationState])
  }
  return $i18n.t('general.queued')
}

function formatStateTime (state: any, stateTime: any) {
  if (!stateTime) { return '' }

  return '@ ' + $date.format($date.local(stateTime * 1000), $date.presets.full)
}

async function handleAction (automation: any, action: any) {
  const actionKey = `${automation.serviceAutomation?.idServiceAutomation}-${action.code}`
  processingAction.value = actionKey

  try {
    const response = await processAutomation(automation.serviceAutomation.idServiceAutomation, action.code)

    if (response) {
      $notification.toast({
        title: $i18n.t('aftercare.actionSuccess', { action: action.label }),
        text: $i18n.t('aftercare.actionSuccessInfo', {
          action: action.label,
          name: automation.serviceAutomation.label
        }),
        variant: 'success',
      })

      emit('update:reservation', response)
    }
  } catch (error) {
    console.error('Error performing action:', error)
    $notification.error({
      title: $i18n.t('aftercare.actionError'),
      text: $i18n.t('aftercare.actionErrorInfo', {
        action: action.label,
        name: automation.serviceAutomation.label
      }),
    })
  } finally {
    processingAction.value = null
  }
}

async function processAutomation (idServiceAutomation: any, code: any) {
  const reservationId = props.reservation.idReservation?.toString()
  return (await $api.reservation.processAutomation(reservationId, idServiceAutomation)).data
}
</script>
